import { Button, Radio } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useState } from "react";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeLoader } from "@/Components/NextBase/LimeLoader";
import { LimeRadioGroup } from "@/Components/NextBase/LimeRadioGroup";
import { planTermLabel } from "@/pages/Dashboard/Subscriptions/commitmentPlanTerm";
import type { SubscriptionDurationUnit } from "@/server-types";

type SignableCommitmentPlan = {
  id: number;
  periods: number;
  priceFormatted: string;
};

type CustomerSubscriptionSignCommitmentSectionProps = {
  plans: SignableCommitmentPlan[];
  durationValue: number;
  durationUnit: SubscriptionDurationUnit;
  blockedReason: string | null;
  isLoadingPlans: boolean;
  hasPlansError: boolean;
  isPending: boolean;
  onSign: (subscriptionCommitmentPlanId: number) => void;
};

export function CustomerSubscriptionSignCommitmentSection({
  plans,
  durationValue,
  durationUnit,
  blockedReason,
  isLoadingPlans,
  hasPlansError,
  isPending,
  onSign,
}: CustomerSubscriptionSignCommitmentSectionProps) {
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);

  if (blockedReason != null) {
    return (
      <p
        data-identifier="sign-commitment-blocked"
        className="text-default-500 text-small"
      >
        {blockedReason}
      </p>
    );
  }

  // An empty list while the catalogue is still in flight is not an answer, and saying
  // the package has no commitment would send the operator away before one arrives.
  if (isLoadingPlans) {
    return (
      <div data-identifier="sign-commitment-plans-loading" className="py-4">
        <LimeLoader />
      </div>
    );
  }

  if (hasPlansError) {
    return (
      <LimeAlert
        color="danger"
        data-identifier="sign-commitment-plans-error"
        message={t`Vezav ni bilo mogoče naložiti. Poskusite ponovno.`}
      />
    );
  }

  if (plans.length === 0) {
    return (
      <p
        data-identifier="sign-commitment-no-plans"
        className="text-default-500 text-small"
      >
        <Trans>Za ta paket ni na voljo nobene vezave.</Trans>
      </p>
    );
  }

  return (
    <div
      data-identifier="sign-commitment-section"
      className="flex flex-col gap-3"
    >
      <p className="text-default-500 text-small">
        <Trans>
          Vezava začne veljati s prvim obdobjem, ki še ni plačano in nima
          izdanega dokumenta.
        </Trans>
      </p>
      <LimeRadioGroup
        data-identifier="sign-commitment-plan-picker"
        label={t`Nova vezava`}
        size="sm"
        orientation="vertical"
        value={selectedPlanId == null ? null : String(selectedPlanId)}
        onValueChange={(value) => setSelectedPlanId(Number(value))}
      >
        {plans.map((plan) => (
          <Radio
            key={plan.id}
            value={String(plan.id)}
            data-identifier={`sign-commitment-plan-option-${plan.id}`}
          >
            <div className="flex flex-col">
              <span>
                {t`Vezava ${planTermLabel(plan.periods, durationValue, durationUnit)}`}
              </span>
              <span className="text-sm text-gray-500">
                {t`${plan.priceFormatted} na obdobje`}
              </span>
            </div>
          </Radio>
        ))}
      </LimeRadioGroup>
      <Button
        color="primary"
        data-identifier="sign-commitment-submit"
        isDisabled={selectedPlanId == null}
        isLoading={isPending}
        onPress={() => {
          if (selectedPlanId != null) {
            onSign(selectedPlanId);
          }
        }}
      >
        <Trans>Skleni vezavo</Trans>
      </Button>
    </div>
  );
}
